import { Box } from "@mui/material";
import MemberOnboardingModule from "components/member/MemberOnboardingModule";
import DigitalSubscriptionLearningModule from "components/member/DigitalSubscriptionLearningModule";

import WelcomeHeader from "components/member/home/WelcomeHeader";
import DigitalCalendarTasks from "components/member/home/DigitalCalendarTasks";
import Documents from "components/member/home/Documents";
import ScheduledActions from "components/member/home/ScheduledActions";
import { ActivityFeed } from "components/member/home/ActivityFeed";
import DelegationList from "components/member/home/DelegationList";
import TodoList from "components/member/home/TodoList";
import FlexBox from "components/common/FlexBox";
import UpcomingVideoCalls from "components/common/UpcomingVideoCalls";
import NoticeBanner from "components/common/NoticeBanner";
import InspirationBanner from "components/member/home/InspirationBanner";
import FeatureSuggestionsModule from "components/member/FeatureSuggestionsModule";
import GatedComponent, {
  FEATURE_FLAGS,
} from "components/helpers/GatedComponent";
import {
  HomeLayoutProps,
  coreDocumentsFromMemberDetails,
  scheduledActionsFromMemberDetails,
  DIGITAL_ONLY_FEATURES,
} from "./types";

export default ({
  data,
  currentUser,
  integrationCount,
  integrationsLoading,
  calendarTwoWaySyncUpsell,
  showAnimations,
  isDesktop,
  isDeactivated,
  productPrompts,
  handleDismissFeatureSuggestions,
  handleDismissLightningBanner,
  handleLightningBannerClick,
  handleDismissReviewPrompt: _handleDismissReviewPrompt,
  handleDismissInspirationBanner,
  openReview: _openReview,
}: HomeLayoutProps) => {
  const coreDocumentItems = coreDocumentsFromMemberDetails(data);
  const scheduledActionItems = scheduledActionsFromMemberDetails(data);

  return (
    <Box sx={{ alignSelf: "center", maxWidth: "1000px", width: "100%" }}>
      <Box
        sx={{
          display: isDesktop ? "grid" : "flex",
          flexDirection: !isDesktop ? "column" : "",
          gridTemplateColumns: {
            sm: "1fr",
            md: "40% 60%",
          },
          gap: "24px",
        }}
      >
        <Box sx={{ gridColumn: "1 / -1" }}>
          <WelcomeHeader user={currentUser} />
        </Box>

        <Box sx={{ gridColumn: "1 / -1" }}>
          <MemberOnboardingModule
            member={data?.member!}
            family={data?.family!}
            showGoogleCalendarSyncUpsell={calendarTwoWaySyncUpsell}
          />
        </Box>

        <Box
          sx={{
            gridColumn: "1 / -1",
            display: "flex",
            flexDirection: "column",
            gap: "8px",
          }}
        >
          <UpcomingVideoCalls family={data?.family!} />
          <GatedComponent
            featureName={FEATURE_FLAGS.DELEGATE_PHONE_CALL.value}
            includeAdmin={true}
            returnNull={true}
          >
            {productPrompts?.lightningTaskBannerDismissed !== "dismissed" && (
              <NoticeBanner
                title="⚡ Need to fire off a quick task?"
                subtext="Create a lightning task for a quick call or other fast action. We will send it to our queue for the first available advisor to pick up."
                buttonText="Try now!"
                dismissible={true}
                onDismiss={handleDismissLightningBanner}
                onClick={handleLightningBannerClick}
              />
            )}
          </GatedComponent>
          {productPrompts?.inspirationBannerDismissed !== "dismissed" && (
            <InspirationBanner onDismiss={handleDismissInspirationBanner} />
          )}
        </Box>

        {!isDeactivated && (
          <Box
            sx={{
              gridColumn: "1 / -1",
              display: "flex",
              flexDirection: "row",
              gap: "24px",
              alignItems: "stretch",
            }}
          >
            <Box
              sx={{
                flex: "1 1 0",
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                gap: "24px",
              }}
            >
              <FlexBox variant="contained" direction="column">
                <Documents documents={coreDocumentItems} />
              </FlexBox>
              <FlexBox variant="contained" direction="column">
                <ScheduledActions actions={scheduledActionItems} />
              </FlexBox>
            </Box>
            <Box
              sx={{
                flex: "1 1 0",
                minWidth: 0,
                background: "white",
                borderRadius: "12px",
                border: "1px solid #D4D4D4",
              }}
            >
              <TodoList data={data} />
            </Box>
          </Box>
        )}

        {!isDeactivated && (
          <Box
            sx={{
              gridColumn: "1 / -1",
              display: "flex",
              flexDirection: "row",
              gap: "24px",
              alignItems: "stretch",
            }}
          >
            <FlexBox
              variant="contained"
              direction="column"
              enableHeightAnimation={true}
              animationDuration="1.5s"
              maxHeight={showAnimations ? "1000px" : "420px"}
              sx={{ flex: 1, minWidth: 0 }}
            >
              <DelegationList />
            </FlexBox>
            <FlexBox
              variant="contained"
              direction="column"
              enableHeightAnimation={true}
              animationDuration="1.5s"
              maxHeight={showAnimations ? "1000px" : "420px"}
              sx={{ flex: 1, minWidth: 0 }}
            >
              <ActivityFeed
                family={data?.family}
                hideActors={true}
                maxEntries={3}
              />
            </FlexBox>
          </Box>
        )}
        {!isDeactivated &&
          productPrompts?.featureSuggestionsDismissed !== "dismissed" && (
            <Box sx={{ gridColumn: "1 / -1" }}>
              <FeatureSuggestionsModule
                features={DIGITAL_ONLY_FEATURES}
                onDismiss={handleDismissFeatureSuggestions}
              />
            </Box>
          )}
        {!isDeactivated && (
          <Box sx={{ gridColumn: "1 / -1" }}>
            <FlexBox
              variant="contained"
              direction="column"
              enableHeightAnimation={true}
              animationDuration="1.5s"
              maxHeight={showAnimations ? "1000px" : "420px"}
            >
              <DigitalCalendarTasks
                family={data?.family!}
                integrationCount={integrationCount}
                forceLoading={!data || integrationsLoading}
              />
            </FlexBox>
          </Box>
        )}
      </Box>
      <DigitalSubscriptionLearningModule />
    </Box>
  );
};
